<template>
  <div class="solution-list">
    <!-- AI 报价-推荐方案（企业微信手机端，仅查看）：方案模板 / 按客户两种视角 -->
    <mobile-list-header>
      <div class="search-bar">
        <div class="search-input">
          <yq-icon class="icon" name="element-ui_search"/>
          <input v-model="searchInfo.searchKey" :placeholder="searchPlaceholder" @keyup.enter="resetSearch"/>
          <yq-icon v-if="searchInfo.searchKey" class="icon clear" name="element-ui_close" @click="clearKey"/>
        </div>
        <div class="search-btn" @click="resetSearch">搜索</div>
      </div>
      <div class="filter-row">
        <!-- 数据模式：显示当前模式，点一下在「方案模板 / 按客户」之间切换 -->
        <div class="mode-chip" @click="switchMode()">{{ modeLabel }}</div>
      </div>
    </mobile-list-header>

    <mobile-list :key="mode" :loading="loading" :has-more="searchInfo.hasMore"
                 :query-list="queryList" :refresh="resetSearch" ref="mobileList">
      <!-- 按方案模板 -->
      <template v-if="mode === 'template'">
        <list-card class="solution-item" v-for="item in dataList" :key="item.id" :label="item.solutionName"
                   @click="toDetail(item)">
          <template #desc>
            <span class="type-tag">{{ Number(item.solutionType) === 2 ? "定制方案" : "通用方案" }}</span>
            <yq-icon class="item-arrow" name="element-ui_arrow-right"/>
          </template>
          <data-column label="推荐规则">{{ ruleName(item.solutionRule) || "—" }}</data-column>
          <data-column label="方案说明">{{ item.solutionDesc || "—" }}</data-column>
          <data-column label="创建时间">{{ dateTime(item.createdTime) }}</data-column>
        </list-card>
      </template>
      <!-- 按客户 -->
      <template v-else>
        <list-card class="solution-item" v-for="item in dataList" :key="item.id" :label="item.btypeName || '—'"
                   @click="toDetail(item)">
          <template #desc>
            <yq-icon class="item-arrow" name="element-ui_arrow-right"/>
          </template>
          <data-column label="推荐方案">{{ item.solutionName || "—" }}</data-column>
        </list-card>
      </template>
    </mobile-list>
  </div>
</template>

<script>

import {useRoute, useRouter} from "vue-router";
import MobileList from "@/components/qywx/layout/MobileList.vue";
import MobileListHeader from "@/components/qywx/layout/MobileListHeader.vue";
import ListCard from "@/components/qywx/layout/ListCard.vue";
import DataColumn from "@/components/qywx/data/DataColumn.vue";
import YqIcon from "@/components/YqIcon.vue";
import qywxXbotSolutionApi from "@/api/dz-ai/qywxXbotSolutionApi";

// 推荐规则（与 web 端 solutionRule.json 保持一致）
const RULE_LIST = [
  {name: "库存不足推荐", value: "STOCK"},
  {name: "一直推荐", value: "NAME"},
];

export default {
  setup() {
    return {
      router: useRouter(),
      route: useRoute(),
    }
  },
  name: "SolutionListQW",
  components: {
    YqIcon, DataColumn, ListCard, MobileListHeader, MobileList
  },
  data() {
    return {
      // template 方案模板 / customer 按客户
      mode: "template",
      loading: false,
      searchInfo: {
        searchKey: "",
        pageNumber: 0,
        pageSize: 20,
        hasMore: true, // 初始为 true，列表挂载后由无限滚动自动触发首次查询
      },
      dataList: [],
      ruleList: RULE_LIST,
    };
  },
  computed: {
    modeLabel() {
      return this.mode === "template" ? "方案模板" : "按客户";
    },
    searchPlaceholder() {
      return this.mode === "template" ? "方案名称" : "客户/方案名称";
    }
  },
  methods: {
    queryList() {
      this.loading = true;
      this.searchInfo.pageNumber++;
      const params = {
        searchKey: this.searchInfo.searchKey,
        pageNumber: this.searchInfo.pageNumber,
        pageSize: this.searchInfo.pageSize,
      };
      const request = this.mode === "template"
          ? qywxXbotSolutionApi.queryList(params)
          : qywxXbotSolutionApi.queryListByBtype(params);
      request.then(res => {
        this.dataList = this.dataList.concat(res.records || []);
        this.searchInfo.hasMore = res.pageCount > this.searchInfo.pageNumber;
        this.loading = false;
        this.$refs.mobileList.hideRefreshTitle();
      }).catch(() => {
        this.loading = false;
      })
    },
    resetSearch() {
      this.searchInfo.pageNumber = 0;
      this.dataList = [];
      this.searchInfo.hasMore = true;
      this.queryList();
    },
    clearKey() {
      this.searchInfo.searchKey = "";
      this.resetSearch();
    },
    // 不传参就是「切到另一个模式」；换模式等于换数据源
    switchMode(mode) {
      const next = mode || (this.mode === "template" ? "customer" : "template");
      if (this.mode === next) {
        return;
      }
      this.mode = next;
      this.searchInfo.pageNumber = 0;
      this.dataList = [];
      this.searchInfo.hasMore = true;
    },
    // 点击进入方案详情（两种视角都按模板 id 打开，详情只展示方案本身，不展示客户关联）
    toDetail(item) {
      if (!item || !item.id) {
        return;
      }
      this.router.push(`/qywx/quotation/solution/detail?id=${item.id}`);
    },
    ruleName(value) {
      const rule = this.ruleList.find(r => r.value === value);
      return rule ? rule.name : "";
    },
    dateTime(value) {
      return value ? String(value).slice(0, 16) : "—";
    },
  }
};
</script>

<style lang="scss" scoped>
.solution-list{
  height: 100%;
  display: flex;
  flex-direction: column;
}
.search-bar{
  display: flex;
  align-items: center;
  height: 44px;
  padding-left: 12px;
  border-radius: 999px;
  background: var(--yq-background-color);
  overflow: hidden;
  .search-input{
    flex: 1;
    display: flex;
    align-items: center;
    min-width: 0;
    height: 100%;
    padding-right: 12px;
    .icon{
      flex: none;
      color: var(--yq-font-color-desc);
      font-size: var(--yq-font-size-mini-mobile);
      &.clear{
        margin-left: 6px;
      }
    }
    input{
      flex: 1;
      min-width: 0;
      height: 100%;
      margin-left: 6px;
      border: none;
      outline: none;
      background: transparent;
      font-size: var(--yq-font-size-mini-mobile);
      color: var(--yq-font-color-base);
      &::placeholder{
        color: var(--yq-font-color-desc);
      }
    }
  }
  .search-btn{
    flex: none;
    display: flex;
    align-items: center;
    height: 100%;
    padding: 0 18px;
    background: var(--el-color-primary);
    color: #FFF;
    font-size: var(--yq-font-size-mini-mobile);
  }
}
.filter-row{
  display: flex;
  align-items: center;
  gap: 18px;
  margin-top: 10px;
  overflow-x: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar{
    display: none;
  }
}
// 数据模式 chip：用主题色，表示「换个视角看同一份数据」
.mode-chip{
  font-size: 12px;
  color: var(--el-color-primary);
}
.solution-item{
  :deep(.card-title .desc){
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .type-tag{
    flex: none;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: var(--yq-font-size-mini-mobile);
    color: var(--el-color-primary);
    background: var(--el-color-primary-light-9);
  }
  .item-arrow{
    color: var(--yq-font-color-desc);
    font-size: var(--yq-font-size-mini-mobile);
  }
}
</style>
